import {
    assertActionMethodBodyContract,
    assertActionPath,
    assertFlatScalarQuery,
    createDTIHmacSha256,
    createDTISignPayload,
    DTI_DEFAULT_SIGN_HEADER_NAMES,
    DTIError,
    parseDTISignTimestamp,
    resolveActionMethod,
    resolveDTISignHeaderNames,
} from "@napp/dti-core";
import type { ActionParam, ActionResult, DTIAction, DTISignHeaderNames, TContentType, TMethod, TResponseType } from "@napp/dti-core";
import type { z } from "zod";

type TActionSchema = z.ZodType;
type TAnyAction = DTIAction<any, any, any, any, any>;
type InferActionSchema<TSchema> = TSchema extends TActionSchema ? z.infer<TSchema> : void;
type InferClientResult<RSchema, ResponseType extends TResponseType> =
    ResponseType extends "text"
        ? string
        : ResponseType extends "file"
            ? Blob
            : InferActionSchema<RSchema>;

export type DTIClientParam<TAction extends TAnyAction> =
    TAction extends DTIAction<infer _RSchema, infer QSchema, infer BSchema, infer _ResponseType, infer PSchema>
    ? ActionParam<InferActionSchema<QSchema>, InferActionSchema<BSchema>, InferActionSchema<PSchema>>
    : never;

export type DTIClientResult<TAction extends TAnyAction> =
    TAction extends DTIAction<infer RSchema, infer _QSchema, infer _BSchema, infer ResponseType, infer _PSchema>
    ? InferClientResult<RSchema, ResponseType>
    : never;

export type DTIClientAuthParam<TAction extends TAnyAction = TAnyAction> = {
    action: TAction;
    param: DTIClientParam<TAction>;
};

export type DTIClientAuth<TAction extends TAnyAction = TAnyAction> = (
    param: DTIClientAuthParam<TAction>,
) => HeadersInit | undefined | Promise<HeadersInit | undefined>;

export type DTIClientSignParam<TAction extends TAnyAction = TAnyAction> = {
    action: TAction;
    param: DTIClientParam<TAction>;
};

export type DTIClientSignSecret<TAction extends TAnyAction = TAnyAction> =
    | string
    | ((param: DTIClientSignParam<TAction>) => string | Promise<string>);

export type DTIClientSign<TAction extends TAnyAction = TAnyAction> = {
    keyId: string;
    secret: DTIClientSignSecret<TAction>;
    timestamp?: () => string | Date | Promise<string | Date>;
    nonce?: () => string | Promise<string>;
    headerNames?: Partial<DTISignHeaderNames>;
};

export type DTIClientOptions = RequestInit & {
    auth?: DTIClientAuth;
    fetcher?: typeof fetch;
    sign?: DTIClientSign;
};

export type DTIClientCallOptions<TAction extends TAnyAction = TAnyAction> = RequestInit & {
    auth?: DTIClientAuth<TAction>;
    sign?: DTIClientSign<TAction> | false;
};

export type DTIClientDetailedResult<TAction extends TAnyAction> = {
    result: DTIClientResult<TAction>;
    response: Response;
};

type TApiSuccess = {
    success: true;
    data: unknown;
};

type TApiFailure = {
    success: false;
    message?: string;
    code?: string;
    details?: Record<string, unknown>;
};

type TApiResponse = TApiSuccess | TApiFailure;

export class DTIClient {
    private readonly fetcher: typeof fetch;
    private readonly options: DTIClientOptions;
    private readonly globalSignHeaderNames: Readonly<DTISignHeaderNames> | undefined;

    constructor(
        private readonly baseUrl: string,
        options?: DTIClientOptions | typeof fetch,
    ) {
        if (typeof options === "function") {
            this.fetcher = options;
            this.options = {};
            this.globalSignHeaderNames = undefined;
            return;
        }

        this.fetcher = options?.fetcher || fetch;
        this.options = options || {};
        this.globalSignHeaderNames = options?.sign
            ? resolveDTISignHeaderNames(options.sign.headerNames)
            : undefined;
    }



    async call<TAction extends TAnyAction>(
        action: TAction,
        param: DTIClientParam<TAction>,
        options?: DTIClientCallOptions<TAction>,
    ): Promise<DTIClientResult<TAction>> {
        const detailed = await this.callDetailed(action, param, options);

        return detailed.result;
    }

    async callDetailed<TAction extends TAnyAction>(
        action: TAction,
        param: DTIClientParam<TAction>,
        options?: DTIClientCallOptions<TAction>,
    ): Promise<DTIClientDetailedResult<TAction>> {
        assertActionPath(action.path);
        assertActionMethodBodyContract(action.method, action.body);
        const method = resolveActionMethod(action);
        const validParam = this.parseParam(action, param);

        if (action.validateParam && !action.validateParam(validParam)) {
            throw new DTIError("Invalid action param", { code: "DTI_PARAM_VALIDATE_ERROR" });
        }

        const { data, response } = await this.callApi(
            this.createUrl(action.path, validParam),
            method,
            action.contentType || "json",
            action.responseType || "json",
            validParam.body,
            action,
            validParam,
            options,
        );

        const result = this.parseResult(action, data);

        if (action.validateResult && !this.validateResult(action.validateResult, result)) {
            throw new DTIError("Invalid action result", { code: "DTI_RESULT_VALIDATE_ERROR" });
        }

        return {
            result,
            response,
        };
    }

    private parseParam<TAction extends TAnyAction>(
        action: TAction,
        param: DTIClientParam<TAction>,
    ): DTIClientParam<TAction> {
        const parsedParam = {} as DTIClientParam<TAction>;

        if (action.params) {
            const parsedPathParams = action.params.safeParse(param.params);
            if (!parsedPathParams.success) {
                throw new DTIError("Invalid action path params", { code: "DTI_PATH_PARAMS_VALIDATE_ERROR" });
            }
            parsedParam.params = parsedPathParams.data;
        }

        if (action.query) {
            assertFlatScalarQuery(param.query);
            const parsedQuery = action.query.safeParse(param.query);
            if (!parsedQuery.success) {
                throw new DTIError("Invalid action query", { code: "DTI_QUERY_VALIDATE_ERROR" });
            }
            assertFlatScalarQuery(parsedQuery.data);
            parsedParam.query = parsedQuery.data;
        }

        if (action.body) {
            const parsedBody = action.body.safeParse(param.body);
            if (!parsedBody.success) {
                throw new DTIError("Invalid action body", { code: "DTI_BODY_VALIDATE_ERROR" });
            }
            parsedParam.body = parsedBody.data;
        }

        return parsedParam;
    }

    private validateResult<TResult>(
        validate: (param: ActionResult<TResult>) => boolean,
        result: TResult,
    ): boolean {
        return validate({ result } as ActionResult<TResult>);
    }

    private parseResult<TAction extends TAnyAction>(
        action: TAction,
        data: unknown,
    ): DTIClientResult<TAction> {
        const responseType = action.responseType || "json";
        if (responseType !== "json") {
            return data as DTIClientResult<TAction>;
        }

        if (!action.result) {
            return undefined as DTIClientResult<TAction>;
        }

        const parsedResult = action.result.safeParse(data);
        if (!parsedResult.success) {
            throw new DTIError("Invalid action result", { code: "DTI_RESULT_PARSE_ERROR" });
        }

        return parsedResult.data as DTIClientResult<TAction>;
    }

    private async callApi<TAction extends TAnyAction>(
        url: string,
        method: TMethod,
        contentType: TContentType,
        responseType: TResponseType,
        body: unknown | undefined,
        action: TAction,
        param: DTIClientParam<TAction>,
        options?: DTIClientCallOptions<TAction>,
    ): Promise<{ data: unknown; response: Response }> {
        try {
            const requestBody = this.createBody(contentType, body);
            const headers = await this.createHeaders(url, method, contentType, body, action, param, options);
            const requestInit = this.createRequestInit(method, requestBody, headers, options);

            const response = await this.fetcher(this.baseUrl + url, requestInit);

            if (!response.ok) {
                throw await this.createResponseError(response);
            }

            if (response.status === 204 || response.status === 205) {
                return {
                    data: undefined,
                    response,
                };
            }

            if (responseType === "text") {
                return {
                    data: await response.text(),
                    response,
                };
            }

            if (responseType === "file") {
                return {
                    data: await response.blob(),
                    response,
                };
            }

            const result = await response.json() as TApiResponse;

            if (result && typeof result === "object" && "success" in result) {
                if (result.success === true) {
                    return {
                        data: result.data,
                        response,
                    };
                }
                throw this.createApiError(result.message || "API error", result.code || "API_ERROR", result.details);
            }

            throw new DTIError("API not supported format", { code: "API_FORMAT_ERROR" });
        } catch (error) {
            if (error instanceof DTIError) {
                throw error;
            }
            throw new DTIError("Network error", { code: "NETWORK_ERROR" });
        }
    }

    private async createResponseError(response: Response): Promise<DTIError> {
        try {
            const result = await response.json() as TApiFailure;
            if (result && typeof result === "object" && result.success === false) {
                return this.createApiError(
                    result.message || response.statusText || "API error",
                    result.code || "API_ERROR",
                    result.details,
                    response.status,
                );
            }
        } catch {
            // Ignore non-JSON error body and fall back to HTTP status.
        }

        return new DTIError(response.statusText || "API server error", {
            code: "API_SERVER_ERROR",
            status: response.status,
        });
    }

    private createApiError(
        message: string,
        code: string,
        details?: Record<string, unknown>,
        status?: number,
    ): DTIError {
        const options: { code: string; details?: Record<string, unknown>; status?: number } = { code };
        if (details) {
            options.details = details;
        }
        if (status) {
            options.status = status;
        }

        return new DTIError(message, options);
    }

    private createUrl(path: string, param: { params?: unknown; query?: unknown }): string {
        const resolvedPath = this.resolvePathParams(path, param.params);
        const query = param.query;
        if (query === undefined) {
            return resolvedPath;
        }
        assertFlatScalarQuery(query);

        const search = new URLSearchParams();
        for (const [key, value] of Object.entries(query)) {
            if (value !== undefined) {
                search.append(key, String(value));
            }
        }

        const queryString = search.toString();
        if (!queryString) {
            return resolvedPath;
        }

        return resolvedPath.includes("?") ? `${resolvedPath}&${queryString}` : `${resolvedPath}?${queryString}`;
    }

    private resolvePathParams(path: string, params: unknown): string {
        const values = params && typeof params === "object"
            ? params as Record<string, unknown>
            : {};

        return path.split("/").map((segment) => {
            const match = /^:([A-Za-z_][A-Za-z0-9_]*)$/.exec(segment);
            if (!match) {
                return segment;
            }

            const paramName = match[1] as string;
            const value = values[paramName];
            const valueType = typeof value;
            const isScalar = valueType === "string"
                || valueType === "number"
                || valueType === "boolean"
                || valueType === "bigint";

            if (!Object.prototype.hasOwnProperty.call(values, paramName)
                || value === null
                || value === undefined
                || !isScalar
                || (valueType === "number" && !Number.isFinite(value))) {
                throw new DTIError(`Invalid action path param: ${paramName}`, {
                    code: "DTI_PATH_PARAMS_VALIDATE_ERROR",
                    details: { paramName },
                });
            }

            return encodeURIComponent(String(value));
        }).join("/");
    }

    private appendFormValue(search: URLSearchParams, key: string, value: unknown): void {
        if (value === undefined || value === null) {
            return;
        }

        if (Array.isArray(value)) {
            for (const item of value) {
                this.appendFormValue(search, key, item);
            }
            return;
        }

        if (typeof value === "object") {
            search.append(key, JSON.stringify(value));
            return;
        }

        search.append(key, String(value));
    }

    private createRequestInit<TAction extends TAnyAction>(
        method: TMethod,
        body: BodyInit | undefined,
        headers: HeadersInit,
        options?: DTIClientCallOptions<TAction>,
    ): RequestInit {
        const {
            auth: _globalAuth,
            body: _globalBody,
            fetcher: _globalFetcher,
            headers: _globalHeaders,
            method: _globalMethod,
            sign: _globalSign,
            ...globalFetchOptions
        } = this.options;
        const {
            auth: _callAuth,
            body: _callBody,
            headers: _callHeaders,
            method: _callMethod,
            sign: _callSign,
            ...callFetchOptions
        } = options || {};

        const requestInit: RequestInit = {
            ...globalFetchOptions,
            ...callFetchOptions,
            method,
            headers,
        };

        if (body !== undefined) {
            requestInit.body = body;
        }

        return requestInit;
    }

    private async createHeaders<TAction extends TAnyAction>(
        url: string,
        method: TMethod,
        contentType: TContentType,
        body: unknown,
        action: TAction,
        param: DTIClientParam<TAction>,
        options?: DTIClientCallOptions<TAction>,
    ): Promise<HeadersInit> {
        const headers = new Headers();

        this.appendHeaders(headers, this.options.headers);
        this.appendHeaders(headers, await this.resolveAuthHeaders(this.options.auth, action, param));
        this.appendHeaders(headers, options?.headers);
        this.appendHeaders(headers, await this.resolveAuthHeaders(options?.auth, action, param));
        this.appendHeaders(headers, await this.createSignHeaders(url, method, action, param, options));

        if (contentType === "form" && this.isFormData(body)) {
            return headers;
        }

        if (!headers.has("Content-Type")) {
            headers.set("Content-Type", this.getContentType(contentType));
        }

        return headers;
    }

    private async createSignHeaders<TAction extends TAnyAction>(
        url: string,
        method: TMethod,
        action: TAction,
        param: DTIClientParam<TAction>,
        options?: DTIClientCallOptions<TAction>,
    ): Promise<HeadersInit | undefined> {
        const sign = options?.sign === false
            ? undefined
            : options?.sign ?? this.options.sign;
        if (!sign) {
            return undefined;
        }
        const headerNames = options?.sign !== undefined && options.sign !== false
            ? resolveDTISignHeaderNames(options.sign.headerNames)
            : this.globalSignHeaderNames ?? DTI_DEFAULT_SIGN_HEADER_NAMES;

        if (!action.signature) {
            throw new DTIError("Action signature is required", { code: "DTI_SIGNATURE_REQUIRED" });
        }

        const timestamp = this.normalizeTimestamp(sign.timestamp ? await sign.timestamp() : new Date());
        const nonce = sign.nonce ? await sign.nonce() : this.createNonce();
        const actionSignature = action.signature(param);
        const signPayload = createDTISignPayload({
            method,
            pathWithQuery: url,
            timestamp,
            nonce,
            actionSignature,
        });
        const secret = await this.resolveSignSecret(sign, action, param);
        const signature = await createDTIHmacSha256(secret, signPayload);

        return {
            [headerNames.keyId]: sign.keyId,
            [headerNames.timestamp]: timestamp,
            [headerNames.nonce]: nonce,
            [headerNames.signature]: signature,
        };
    }

    private async resolveSignSecret<TAction extends TAnyAction>(
        sign: DTIClientSign<TAction> | DTIClientSign,
        action: TAction,
        param: DTIClientParam<TAction>,
    ): Promise<string> {
        if (typeof sign.secret === "function") {
            return await sign.secret({ action, param });
        }

        return sign.secret;
    }

    private normalizeTimestamp(timestamp: string | Date): string {
        let normalizedTimestamp: string;
        try {
            normalizedTimestamp = timestamp instanceof Date ? timestamp.toISOString() : timestamp;
        } catch {
            throw new DTIError("Request signature timestamp is invalid", {
                code: "DTI_SIGNATURE_TIMESTAMP_INVALID",
            });
        }

        if (typeof normalizedTimestamp !== "string" || parseDTISignTimestamp(normalizedTimestamp) === undefined) {
            throw new DTIError("Request signature timestamp is invalid", {
                code: "DTI_SIGNATURE_TIMESTAMP_INVALID",
            });
        }

        return normalizedTimestamp;
    }

    private createNonce(): string {
        const crypto = globalThis.crypto;
        if (crypto?.randomUUID) {
            return crypto.randomUUID();
        }

        if (crypto?.getRandomValues) {
            const bytes = new Uint8Array(16);
            crypto.getRandomValues(bytes);

            return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
        }

        return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    private async resolveAuthHeaders<TAction extends TAnyAction>(
        auth: DTIClientAuth<TAction> | DTIClientAuth | undefined,
        action: TAction,
        param: DTIClientParam<TAction>,
    ): Promise<HeadersInit | undefined> {
        if (!auth) {
            return undefined;
        }

        return await auth({ action, param });
    }

    private appendHeaders(headers: Headers, source?: HeadersInit): void {
        if (!source) {
            return;
        }

        new Headers(source).forEach((value, key) => {
            headers.set(key, value);
        });
    }

    private getContentType(contentType: TContentType): string {
        switch (contentType) {
            case "form":
                return "application/x-www-form-urlencoded";
            case "text":
                return "text/plain";
            case "json":
            default:
                return "application/json";
        }
    }

    private createBody(contentType: TContentType, body: unknown): BodyInit | undefined {
        if (body === undefined || body === null) {
            return undefined;
        }

        switch (contentType) {
            case "form":
                return this.createFormBody(body);
            case "text":
                return typeof body === "string" ? body : String(body);
            case "json":
            default:
                return JSON.stringify(body);
        }
    }

    private createFormBody(body: unknown): BodyInit {
        if (this.isFormData(body) || body instanceof URLSearchParams) {
            return body;
        }

        if (body === null || typeof body !== "object") {
            return String(body);
        }

        const form = new URLSearchParams();
        for (const [key, value] of Object.entries(body)) {
            this.appendFormValue(form, key, value);
        }

        return form;
    }

    private isFormData(value: unknown): value is FormData {
        return typeof FormData !== "undefined" && value instanceof FormData;
    }
}
